import { normalizeCode } from "./deriveWorkspaces";
import {
  DEFAULT_WORKSPACE_GROUP_KEY,
  normalizeWorkspaceGroupKey,
} from "./clientGroups";

export type WorkspaceRouteContext = {
  group: string;
  workspace: string;
};

function normalizeWorkspaceForPath(
  workspace: string | null | undefined,
): string {
  return (workspace ?? "").trim().toLowerCase();
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function normalizeWorkspaceGroupForPath(
  group: string | null | undefined,
): string {
  return (
    normalizeWorkspaceGroupKey(group, { fallbackToDefault: true }) ??
    DEFAULT_WORKSPACE_GROUP_KEY
  );
}

export function buildWorkspaceDashboardPath(input: {
  group: string | null | undefined;
  workspace: string;
  uuid?: string | null;
  view?: string | null;
}): string {
  const group = encodeURIComponent(normalizeWorkspaceGroupForPath(input.group));
  const workspace = encodeURIComponent(
    normalizeWorkspaceForPath(input.workspace),
  );
  const base = `/w/${group}/${workspace}/dashboard`;
  const withUuid = input.uuid ? `${base}/${input.uuid}` : base;
  if (!input.view) return withUuid;
  return `${withUuid}?view=${encodeURIComponent(input.view)}`;
}

export function buildWorkspaceAdminPath(input: {
  group: string | null | undefined;
  workspace: string;
  path?: string;
}): string {
  const group = encodeURIComponent(normalizeWorkspaceGroupForPath(input.group));
  const workspace = encodeURIComponent(
    normalizeWorkspaceForPath(input.workspace),
  );
  const rest = (input.path ?? "").replace(/^\/+/, "");
  const segments = rest.split("/").filter(Boolean);
  const [first, second, ...tail] = segments;
  let normalizedRest = rest;
  const firstCode = normalizeCode(first);
  const workspaceCode = normalizeCode(input.workspace);
  const secondLower = second?.toLowerCase() ?? "";
  if (
    firstCode &&
    workspaceCode &&
    firstCode === workspaceCode &&
    secondLower.startsWith(`${firstCode}_`)
  ) {
    normalizedRest = [second, ...tail].join("/");
  }

  const base = `/w/${group}/${workspace}/admin`;
  return normalizedRest ? `${base}/${normalizedRest}` : base;
}

export function toWorkspacePath(
  path: string,
  pathname?: string | null,
): string {
  if (!path.startsWith("/")) return path;
  if (!path.startsWith("/admin/") && !path.startsWith("/dashboard")) {
    return path;
  }
  const context = parseWorkspaceContextFromPathname(
    pathname ?? (typeof window !== "undefined" ? window.location.pathname : ""),
  );
  if (!context) return path;

  if (path.startsWith("/admin/")) {
    const [adminPath, suffix = ""] = path.split(/(?=[?#])/u, 2);
    return buildWorkspaceAdminPath({
      group: context.group,
      workspace: context.workspace,
      path: adminPath.replace(/^\/admin\/?/, ""),
    }) + suffix;
  }

  const [dashboardPath, query = ""] = path.split("?", 2);
  const dashboardParts = dashboardPath.split("/").filter(Boolean);
  const uuid = dashboardParts[1] ?? null;
  const params = new URLSearchParams(query);
  const view = params.get("view");
  return buildWorkspaceDashboardPath({
    group: context.group,
    workspace: context.workspace,
    uuid,
    view,
  });
}

export function parseWorkspaceContextFromPathname(
  pathname: string,
): WorkspaceRouteContext | null {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] !== "w" || parts.length < 3) return null;
  const group = normalizeWorkspaceGroupForPath(safeDecode(parts[1]));
  const workspace = normalizeWorkspaceForPath(safeDecode(parts[2]));
  if (!workspace) return null;
  return { group, workspace };
}
